import { useMemo } from 'react';
import {
  Brush,
  ComposedChart,
  CartesianGrid,
  ErrorBar,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PIPEFITTER_CHART_COLORS } from '../constants/uiConstants';
import { ChartBrushZoomControls, useChartBrushZoom } from './chartBrushZoom';

export interface PipeDataGraphItem {
  pipeEndID?: string | number;
  avgID?: string | number;
  maxID?: string | number;
  minID?: string | number;
  avgOD?: string | number;
  maxOD?: string | number;
  minOD?: string | number;
  ooR?: string | number | null;
  ooROD?: string | number | null;
  oor?: string | number | null;
  oorod?: string | number | null;
}

interface PipeDataChartProps {
  pipeDataGraph: PipeDataGraphItem[];
  showIDData: boolean;
  onIDODChange?: (showID: boolean) => void;
  /** Less padding when embedded in a parent card */
  minimal?: boolean;
  /** OoR line + right axis; defaults to off in minimal/report embeds */
  showOor?: boolean;
}

type ChartRow = {
  pipeEndID: string;
  avg: number;
  min: number;
  max: number;
  oor: number;
  error: [number, number];
};

const parse = (v: string | number | null | undefined): number =>
  typeof v === 'number' ? v : parseFloat(String(v ?? '')) || 0;

function buildErrorRange(min: number, max: number, avg: number): [number, number] {
  const errLow = Math.max(0, avg - min);
  const errHigh = Math.max(0, max - avg);
  if (errLow === 0 && errHigh === 0) return [0.001, 0.001];
  return [errLow, errHigh];
}

function niceStep(span: number): number {
  if (!Number.isFinite(span) || span <= 0) return 1;
  const rough = span / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  let nice = 10;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  return nice * magnitude;
}

function computeYDomain(data: ChartRow[]): [number, number] {
  const values = data.flatMap((d) => [d.min, d.max, d.avg]).filter((n) => n > 0);
  if (!values.length) return [0, 1];

  let lo = Math.min(...values);
  let hi = Math.max(...values);

  const minSpan = Math.max(2, hi * 0.005);
  if (hi - lo < minSpan) {
    const mid = (lo + hi) / 2;
    lo = mid - minSpan / 2;
    hi = mid + minSpan / 2;
  } else {
    const pad = Math.max((hi - lo) * 0.12, 0.25);
    lo -= pad;
    hi += pad;
  }

  const step = niceStep(hi - lo);
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step;
  if (hi <= lo) hi = lo + step;

  return [lo, hi];
}

function computeOorDomain(values: number[]): [number, number] {
  const positives = values.filter((n) => n > 0);
  if (!positives.length) return [0, 1];
  const hi = Math.max(...positives);
  const pad = Math.max(hi * 0.15, 0.05);
  return [0, hi + pad];
}

function yAxisDecimals(domain: [number, number]): number {
  const span = domain[1] - domain[0];
  if (span <= 2) return 3;
  if (span <= 20) return 2;
  return 1;
}

function oorAxisDecimals(domain: [number, number]): number {
  const span = domain[1] - domain[0];
  if (span <= 1) return 3;
  if (span <= 5) return 2;
  return 1;
}

function AvgCrosshairDot({ cx, cy }: { cx?: number; cy?: number }) {
  if (cx == null || cy == null) return null;
  const arm = 10;

  return (
    <g>
      <line
        x1={cx - arm}
        y1={cy}
        x2={cx + arm}
        y2={cy}
        stroke={PIPEFITTER_CHART_COLORS.primary}
        strokeWidth={3}
        strokeLinecap="round"
      />
      <circle
        cx={cx}
        cy={cy}
        r={3.5}
        fill={PIPEFITTER_CHART_COLORS.primary}
        stroke="white"
        strokeWidth={1}
      />
    </g>
  );
}

const RangeTooltip = ({
  active,
  payload,
  label,
  showOor,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
  label?: string;
  showOor?: boolean;
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;

  return (
    <div
      style={{
        background: 'white',
        border: `1px solid ${PIPEFITTER_CHART_COLORS.border}`,
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 11,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <p style={{ color: 'black', marginBottom: 6, fontWeight: 600, wordBreak: 'break-all' }}>{label}</p>
      <p style={{ color: PIPEFITTER_CHART_COLORS.gray, margin: '2px 0' }}>
        Min: {row.min.toFixed(2)} mm
      </p>
      <p style={{ color: PIPEFITTER_CHART_COLORS.primary, margin: '2px 0' }}>
        Avg: {row.avg.toFixed(2)} mm
      </p>
      <p style={{ color: PIPEFITTER_CHART_COLORS.secondaryOrange, margin: '2px 0' }}>
        Max: {row.max.toFixed(2)} mm
      </p>
      {showOor && (
        <p style={{ color: PIPEFITTER_CHART_COLORS.secondaryGreen, margin: '2px 0' }}>
          OoR: {row.oor.toFixed(2)} mm
        </p>
      )}
    </div>
  );
};

export default function PipeDataChart({
  pipeDataGraph,
  showIDData,
  onIDODChange,
  minimal = false,
  showOor,
}: PipeDataChartProps) {
  const data: ChartRow[] = useMemo(
    () =>
      pipeDataGraph
        .map((d) => {
          const avg = showIDData ? parse(d.avgID) : parse(d.avgOD);
          const min = showIDData ? parse(d.minID) : parse(d.minOD);
          const max = showIDData ? parse(d.maxID) : parse(d.maxOD);
          const oor = showIDData ? parse(d.ooR ?? d.oor) : parse(d.ooROD ?? d.oorod);
          return {
            pipeEndID: String(d.pipeEndID ?? ''),
            avg,
            min,
            max,
            oor,
            error: buildErrorRange(min, max, avg),
          };
        })
        .filter((row) => row.pipeEndID && (row.avg > 0 || row.min > 0 || row.max > 0)),
    [pipeDataGraph, showIDData]
  );

  const zoom = useChartBrushZoom(data.length);
  const includeOor = (showOor ?? !minimal) && data.some((row) => row.oor > 0);
  const dimensionLabel = showIDData
    ? minimal
      ? 'Inner Diameter (mm)'
      : 'ID (mm)'
    : minimal
      ? 'Outer Diameter (mm)'
      : 'OD (mm)';
  const yDomain = computeYDomain(data);
  const yDecimals = yAxisDecimals(yDomain);
  const oorDomain = computeOorDomain(data.map((row) => row.oor));
  const oorDecimals = oorAxisDecimals(oorDomain);
  const xTickInterval = zoom.visibleCount > 40 ? Math.ceil(zoom.visibleCount / 24) - 1 : 0;

  if (data.length === 0) {
    return null;
  }

  const chartMargin = {
    top: 10,
    right: includeOor ? 52 : minimal ? 40 : 24,
    left: 10,
    bottom: minimal ? 92 : 76,
  };

  return (
    <div style={{ padding: minimal ? 0 : '10px 0' }}>
      {onIDODChange && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <div
            style={{
              display: 'flex',
              gap: 0,
              background: 'white',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #333',
            }}
          >
            <button
              type="button"
              onClick={() => onIDODChange(true)}
              style={{
                padding: '8px 24px',
                background: showIDData ? 'white' : 'transparent',
                color: showIDData ? 'black' : '#666',
                border: 'none',
                borderRight: '1px solid #333',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: showIDData ? 700 : 400,
              }}
            >
              ID
            </button>
            <button
              type="button"
              onClick={() => onIDODChange(false)}
              style={{
                padding: '8px 24px',
                background: !showIDData ? 'white' : 'transparent',
                color: !showIDData ? 'black' : '#666',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: !showIDData ? 700 : 400,
              }}
            >
              OD
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          background: 'white',
          borderRadius: minimal ? 8 : 16,
          padding: minimal ? '8px 12px 8px' : '20px 16px 12px',
          width: '100%',
          border: `1px solid ${PIPEFITTER_CHART_COLORS.border}`,
        }}
      >
        <div className="mb-2 px-1">
          <ChartBrushZoomControls
            canZoomIn={zoom.canZoomIn}
            canZoomOut={zoom.canZoomOut}
            isZoomed={zoom.isZoomed}
            onZoomIn={zoom.zoomIn}
            onZoomOut={zoom.zoomOut}
            onReset={zoom.resetZoom}
            visibleCount={zoom.visibleCount}
            totalCount={data.length}
          />
        </div>
        <ResponsiveContainer width="100%" height={minimal ? 360 : 390}>
          <ComposedChart data={data} margin={chartMargin}>
            <CartesianGrid strokeDasharray="3 3" stroke={PIPEFITTER_CHART_COLORS.border} vertical={false} />
            <XAxis
              dataKey="pipeEndID"
              type="category"
              padding={{ left: 8, right: 28 }}
              tick={{ fill: 'black', fontSize: 9 }}
              interval={xTickInterval}
              angle={-35}
              textAnchor="middle"
              height={70}
              tickMargin={4}
              tickLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              axisLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              label={
                minimal
                  ? {
                      value: 'Pipe Profile Reference',
                      position: 'insideBottom',
                      offset: -6,
                      fill: PIPEFITTER_CHART_COLORS.grayDark,
                      fontSize: 10,
                    }
                  : undefined
              }
            />
            <YAxis
              yAxisId="dimension"
              domain={yDomain}
              tick={{ fill: 'black', fontSize: 10 }}
              tickFormatter={(v: number) => v.toFixed(yDecimals)}
              tickCount={6}
              tickLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              axisLine={{ stroke: PIPEFITTER_CHART_COLORS.border }}
              label={{
                value: dimensionLabel,
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                fill: 'black',
                fontSize: 10,
              }}
            />
            {includeOor && (
              <YAxis
                yAxisId="oor"
                orientation="right"
                domain={oorDomain}
                tick={{ fill: PIPEFITTER_CHART_COLORS.secondaryGreen, fontSize: 10 }}
                tickFormatter={(v: number) => v.toFixed(oorDecimals)}
                tickCount={5}
                tickLine={{ stroke: PIPEFITTER_CHART_COLORS.secondaryGreen }}
                axisLine={{ stroke: PIPEFITTER_CHART_COLORS.secondaryGreen }}
                label={{
                  value: 'OoR (mm)',
                  angle: 90,
                  position: 'insideRight',
                  offset: 10,
                  fill: PIPEFITTER_CHART_COLORS.secondaryGreen,
                  fontSize: 10,
                }}
              />
            )}
            <Tooltip
              content={<RangeTooltip showOor={includeOor} />}
              cursor={{ stroke: PIPEFITTER_CHART_COLORS.border, strokeWidth: 1 }}
            />
            <Line
              yAxisId="dimension"
              type="monotone"
              dataKey="avg"
              name="Average"
              stroke="transparent"
              strokeWidth={0}
              dot={<AvgCrosshairDot />}
              activeDot={{ r: 6, fill: PIPEFITTER_CHART_COLORS.primary, stroke: 'white', strokeWidth: 1 }}
              isAnimationActive={false}
            >
              <ErrorBar
                dataKey="error"
                direction="y"
                width={8}
                strokeWidth={2.5}
                stroke={PIPEFITTER_CHART_COLORS.gray}
              />
            </Line>
            {includeOor && (
              <Line
                yAxisId="oor"
                type="monotone"
                dataKey="oor"
                name="OoR"
                stroke={PIPEFITTER_CHART_COLORS.secondaryGreen}
                strokeWidth={2}
                dot={{
                  r: 4,
                  fill: PIPEFITTER_CHART_COLORS.secondaryGreen,
                  stroke: 'white',
                  strokeWidth: 1,
                }}
                activeDot={{
                  r: 5,
                  fill: PIPEFITTER_CHART_COLORS.secondaryGreen,
                  stroke: 'white',
                  strokeWidth: 1,
                }}
                isAnimationActive={false}
              />
            )}
            <Brush
              dataKey="pipeEndID"
              height={26}
              stroke={PIPEFITTER_CHART_COLORS.primary}
              travellerWidth={10}
              startIndex={zoom.startIndex}
              endIndex={zoom.endIndex}
              onChange={zoom.onBrushChange}
              tickFormatter={() => ''}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

